import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { projectExtras } from "@/data/projectExtras";
import ProjectChecklist from "@/components/projects/ProjectChecklist";
import GaleriaDelivery from "@/components/projects/GaleriaDelivery";
import ProjectProgressSummary from "@/components/projects/ProjectProgressSummary";
import GaleriaShareButton from "@/components/GaleriaShareButton";
import {
  Camera,
  User,
  Mountains,
  TShirt,
  Buildings,
  PawPrint,
  Package,
  Moon,
  MagnifyingGlassPlus,
  Lightning,
  UsersThree,
  ForkKnife,
  CaretDown,
  CaretUp,
  Star,
  CircleHalf,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const difficultyColors = {
  beginner: { bg: "bg-green-500/10", text: "text-green-400", border: "border-green-500/20" },
  intermediate: { bg: "bg-[#F5A524]/10", text: "text-[#F5A524]", border: "border-[#F5A524]/20" },
  advanced: { bg: "bg-red-500/10", text: "text-red-400", border: "border-red-500/20" },
};

export const projects = {
  en: {
    page_title: "Photography Projects",
    page_sub: "Hands-on shooting assignments to build your skills. Pick a project, grab your camera, and go shoot!",
    difficulty: "Difficulty",
    objective: "Objective",
    settings_hint: "Suggested Settings",
    tips: "Pro Tips",
    deliverables: "Deliverables",
    beginner: "Beginner",
    intermediate: "Intermediate",
    advanced: "Advanced",
    items: [
      {
        id: "food",
        icon: "ForkKnife",
        title: "Food Photography",
        desc: "Photograph a meal or dish with props and natural light to create an appetizing, magazine-quality image.",
        difficulty: "beginner",
        objective: "Create 5 images of a plated dish from different angles (overhead, 45-degree, and eye-level) using only window light.",
        settings: "ISO 200-400 | f/2.8-f/4 for shallow DOF | 1/125 sec | White balance: Daylight or Cloudy",
        tips: [
          "Shoot near a large window — soft, directional light is the secret.",
          "Use a white board or napkin as a reflector on the shadow side.",
          "Style the plate before shooting: wipe drips, add fresh herbs, drizzle sauce.",
          "Try a 45-degree angle first — it's the most natural 'eating' perspective.",
          "Include contextual props (cutlery, linen, ingredients) but don't clutter.",
        ],
        deliverables: "5 edited images: 1 overhead (flat lay), 2 at 45 degrees, 1 eye-level, 1 detail close-up.",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Portrait Photography",
        desc: "Capture three distinct portrait styles of the same person — headshot, environmental, and creative.",
        difficulty: "beginner",
        objective: "Produce 3 portraits showing different moods using natural light and simple backgrounds.",
        settings: "ISO 100-400 | f/1.8-f/2.8 for background blur | 1/200 sec | 50-85mm focal length",
        tips: [
          "Shoot during golden hour or in open shade for soft, flattering light.",
          "Focus on the eyes — always. Use single-point AF on the nearest eye.",
          "Direct your subject: have them look slightly off-camera for a candid feel.",
          "For headshots, keep the background simple and uncluttered.",
          "For environmental portraits, include surroundings that tell a story about the person.",
        ],
        deliverables: "3 edited portraits: 1 tight headshot, 1 environmental (wider, subject in context), 1 creative (unusual angle, light, or mood).",
      },
      {
        id: "landscape",
        icon: "Mountains",
        title: "Landscape Photography",
        desc: "Capture a golden hour landscape that demonstrates depth, leading lines, and proper exposure.",
        difficulty: "beginner",
        objective: "Shoot 5 landscape images during golden hour showing foreground, midground, and background elements.",
        settings: "ISO 100 | f/8-f/11 for sharpness | Tripod recommended | 1/30-1/125 sec | Wide-angle (16-35mm)",
        tips: [
          "Arrive 30 minutes before golden hour to scout your composition.",
          "Use f/8-f/11 for maximum sharpness across the frame.",
          "Include a strong foreground element (rocks, flowers, water) for depth.",
          "Place the horizon on the upper or lower third — never dead center.",
          "Bracket exposures (+/- 1 stop) in high-contrast scenes.",
        ],
        deliverables: "5 edited landscapes: 2 wide views, 1 with leading lines, 1 with strong foreground, 1 panoramic or vertical.",
      },
      {
        id: "fashion",
        icon: "TShirt",
        title: "Fashion & Street Style",
        desc: "Photograph 5 outfit looks with urban backdrops that highlight both clothing and environment.",
        difficulty: "intermediate",
        objective: "Create a mini lookbook: 5 full-body fashion shots with varied poses, angles, and urban locations.",
        settings: "ISO 100-400 | f/2-f/4 | 1/250 sec | 50-85mm focal length",
        tips: [
          "Scout colorful walls, stairways, or textured surfaces as backdrops.",
          "Shoot at eye level or slightly below to make your model look taller.",
          "Direct movement: walking, turning, adjusting an accessory adds energy.",
          "Ensure the clothing is the hero — background should complement, not compete.",
          "Overcast skies are your friend: even, soft light with no harsh shadows.",
        ],
        deliverables: "5 edited images: mix of full-body and 3/4 shots, at least 2 different locations.",
      },
      {
        id: "street",
        icon: "Camera",
        title: "Street Photography",
        desc: "Document candid moments of everyday life in a public space without staging or directing subjects.",
        difficulty: "intermediate",
        objective: "Shoot 10 candid images in 1 hour that capture emotion, story, or interesting juxtaposition.",
        settings: "ISO 400-800 | f/5.6-f/8 | 1/250 sec | 35-50mm focal length | Zone focus",
        tips: [
          "Pre-set exposure and focus to a zone distance (3-5 meters) so you can shoot fast.",
          "Look for contrasts: old/new, light/shadow, movement/stillness.",
          "Be patient — wait at an interesting location for a moment to unfold.",
          "Shoot from the hip for discreet candid moments.",
          "Respect people's space. If someone is uncomfortable, move on.",
        ],
        deliverables: "10 unedited + 5 selects with basic editing. No staging — all must be candid.",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Architecture Photography",
        desc: "Capture building lines, symmetry, and geometric patterns with precise framing.",
        difficulty: "intermediate",
        objective: "Photograph 5 buildings or structures emphasizing lines, symmetry, and geometric repetition.",
        settings: "ISO 100-200 | f/8-f/11 | Tripod recommended | Wide-angle (16-24mm) or telephoto for compression",
        tips: [
          "Level your camera — tilted verticals are the #1 beginner mistake.",
          "Use a wide-angle lens close up for dramatic perspective, or telephoto from far for compression.",
          "Shoot at blue hour for dramatic sky colors behind lit buildings.",
          "Look for repeating patterns: windows, columns, staircases.",
          "Include a human figure for scale in at least one shot.",
        ],
        deliverables: "5 edited images: 1 symmetrical, 1 with leading lines, 1 detail/pattern, 1 with human scale, 1 at blue hour or dramatic light.",
      },
      {
        id: "wildlife",
        icon: "PawPrint",
        title: "Wildlife & Nature",
        desc: "Photograph animals or insects in their natural habitat with patience and fast reflexes.",
        difficulty: "advanced",
        objective: "Capture 5 images of animals or insects showing behavior, detail, or interaction with environment.",
        settings: "ISO 400-1600 | f/4-f/5.6 | 1/500+ sec for action | 200-400mm telephoto or macro",
        tips: [
          "Move slowly and quietly. Let the animal come to you.",
          "Use continuous autofocus (AF-C) to track moving subjects.",
          "Get down to the animal's eye level for a more intimate perspective.",
          "Shoot in burst mode — behavior happens in split seconds.",
          "Early morning and late afternoon are when animals are most active.",
        ],
        deliverables: "5 edited images: 1 portrait (eye detail), 1 action/behavior, 1 in environment, 1 macro detail, 1 creative (silhouette or backlit).",
      },
      {
        id: "product",
        icon: "Package",
        title: "Product Photography",
        desc: "Shoot clean product images suitable for an online store using simple DIY lighting.",
        difficulty: "beginner",
        objective: "Create 5 product images on a clean background (white or dark) with even, shadow-free lighting.",
        settings: "ISO 100-200 | f/5.6-f/8 | Tripod | 1/60-1/125 sec | 50-100mm",
        tips: [
          "Use a white poster board curved into an \"infinity curve\" for a seamless background.",
          "Two desk lamps with white paper diffusers = affordable studio lighting.",
          "Shoot from multiple angles: front, 45-degree, top-down, detail close-up.",
          "Keep the product clean: fingerprints and dust show in photos.",
          "For reflective products, use a light tent or surround with white cards.",
        ],
        deliverables: "5 edited images per product: front, angle, top, detail, lifestyle context shot.",
      },
      {
        id: "night",
        icon: "Moon",
        title: "Night & Long Exposure",
        desc: "Create dramatic nighttime images using long exposures, light trails, and creative techniques.",
        difficulty: "advanced",
        objective: "Produce 5 night images: light trails, star trails or starry sky, light painting, city at blue hour, and a creative long exposure.",
        settings: "ISO 100-800 | f/8-f/16 for trails, f/2.8 for stars | 10-30 sec (or bulb) | Tripod essential",
        tips: [
          "Use a tripod and remote shutter release (or 2-second timer) to avoid shake.",
          "For light trails: 10-30 seconds at f/8, ISO 100 near a busy road.",
          "For stars: f/2.8, ISO 1600-3200, 15-25 seconds (use 500 Rule: 500/focal length = max seconds).",
          "Shoot at blue hour (20-40 min after sunset) for the best sky-to-city contrast.",
          "Light painting: use a flashlight during a long exposure to 'paint' light onto your subject.",
        ],
        deliverables: "5 edited images: 1 light trail, 1 starry sky, 1 light painting, 1 blue hour cityscape, 1 creative.",
      },
      {
        id: "macro",
        icon: "MagnifyingGlassPlus",
        title: "Macro Photography",
        desc: "Explore the miniature world of extreme close-ups revealing textures and details invisible to the naked eye.",
        difficulty: "intermediate",
        objective: "Capture 5 macro images of different subjects showing texture, pattern, and extreme detail.",
        settings: "ISO 200-400 | f/8-f/11 for more DOF | 1/200+ sec (or tripod) | Macro lens or extension tubes",
        tips: [
          "Depth of field is razor-thin in macro — use f/8 or smaller and focus stack if possible.",
          "Use manual focus and rock your body forward/backward to nail the focus plane.",
          "Overcast days or shade provide even, diffused light that reveals texture.",
          "Flowers, insects, water drops, food textures, and fabric patterns are great subjects.",
          "A tripod with a ball head is nearly essential for sharp macro work.",
        ],
        deliverables: "5 edited macro images: 1 insect/bug, 1 flower detail, 1 texture (fabric/wood/metal), 1 water drop, 1 food close-up.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Sports & Action",
        desc: "Freeze fast-moving subjects with high shutter speeds and continuous tracking autofocus.",
        difficulty: "advanced",
        objective: "Capture 5 action shots that freeze peak motion and convey energy and athleticism.",
        settings: "ISO 400-1600 | f/2.8-f/4 | 1/1000+ sec | AF-C continuous tracking | 70-200mm",
        tips: [
          "Use Shutter Priority (Tv/S) mode: set 1/1000+ and let the camera choose aperture.",
          "Continuous AF (AF-C / AI Servo) is non-negotiable for moving subjects.",
          "Anticipate the peak moment: a basketball player at the top of their jump, a runner crossing the finish.",
          "Pan with the subject for motion blur in the background but a sharp subject.",
          "Shoot in burst mode — capture 5-10 frames per second to catch the decisive moment.",
        ],
        deliverables: "5 edited action images: 1 frozen peak moment, 1 panning shot (blurred background), 1 pre-game preparation, 1 celebration/emotion, 1 wide establishing shot.",
      },
      {
        id: "event",
        icon: "UsersThree",
        title: "Event Photography",
        desc: "Document a social gathering by capturing key moments, candid emotions, and group shots.",
        difficulty: "intermediate",
        objective: "Cover a social event (party, family gathering, school event) with a variety of shots that tell the story.",
        settings: "ISO 800-3200 | f/2.8-f/4 | 1/125-1/250 sec | Flash bounce if indoors | 24-70mm",
        tips: [
          "Arrive early to scout lighting and find good angles before guests arrive.",
          "Capture the three phases: setup/anticipation, key moments, candid reactions.",
          "Bounce your flash off the ceiling for soft, natural-looking indoor light.",
          "Get both wide shots (environment/atmosphere) and tight shots (emotion/detail).",
          "Keep moving — don't stay in one spot the entire event.",
        ],
        deliverables: "10 edited images: 2 wide venue/atmosphere, 3 candid moments, 2 posed groups, 2 detail/decor shots, 1 creative (silhouette, reflection, or motion blur).",
      },
      {
        id: "blackwhite",
        icon: "CircleHalf",
        title: "Black & White Photography",
        desc: "Shoot a series of frames using only monochrome — focus on tonal range, contrast, shadow shapes, and texture instead of color.",
        difficulty: "intermediate",
        objective: "Create a cohesive set of 8 monochrome images where light, contrast, and texture — not color — carry the entire frame.",
        settings: "ISO 100-800 | Shoot RAW + set camera Picture Style/Profile to Monochrome for a live B&W preview | Meter for the highlights | Any focal length",
        tips: [
          "Train your eye to see tones, not colors: squint at the scene to judge how light and dark areas separate.",
          "Hunt for contrast and texture — side light at golden hour reveals shape, grit, and form that color hides.",
          "Use the camera's Monochrome Picture Style so the rear screen previews B&W, but keep shooting RAW to retain full data for conversion.",
          "Shadows are a subject: let blacks go truly black and use negative space deliberately.",
          "Color filters (or their digital equivalents) change tonal relationships — a red filter darkens blue skies for dramatic clouds.",
        ],
        deliverables: "8 edited monochrome images: 2 high-contrast, 2 low-key (shadow-dominant), 2 texture/detail studies, 1 high-key (bright), 1 environmental portrait.",
      },
    ],
  },
  es: {
    page_title: "Proyectos Fotográficos",
    page_sub: "Asignaciones prácticas de fotografía para desarrollar tus habilidades. Elige un proyecto, toma tu cámara y sal a disparar!",
    difficulty: "Dificultad",
    objective: "Objetivo",
    settings_hint: "Ajustes Sugeridos",
    tips: "Consejos Pro",
    deliverables: "Entregables",
    beginner: "Principiante",
    intermediate: "Intermedio",
    advanced: "Avanzado",
    items: [
      {
        id: "food",
        icon: "ForkKnife",
        title: "Fotografía de Alimentos",
        desc: "Fotografiar un plato con accesorios y luz natural para crear una imagen apetitosa de calidad editorial.",
        difficulty: "beginner",
        objective: "Crear 5 imágenes de un plato desde diferentes ángulos (cenital, 45 grados y nivel de ojo) usando solo luz de ventana.",
        settings: "ISO 200-400 | f/2.8-f/4 para poca profundidad | 1/125 seg | Balance de blancos: Luz de Día o Nublado",
        tips: [
          "Dispara cerca de una ventana grande — la luz suave y direccional es el secreto.",
          "Usa una cartulina blanca o servilleta como reflector en el lado de la sombra.",
          "Prepara el plato antes de disparar: limpia gotas, añade hierbas frescas, rociar salsa.",
          "Prueba un ángulo de 45 grados primero — es la perspectiva más natural para 'comer'.",
          "Incluye accesorios contextuales (cubiertos, mantel, ingredientes) pero no satures.",
        ],
        deliverables: "5 imágenes editadas: 1 cenital (flat lay), 2 a 45 grados, 1 a nivel de ojo, 1 detalle de cerca.",
      },
      {
        id: "portrait",
        icon: "User",
        title: "Fotografía de Retratos",
        desc: "Captura tres estilos distintos de retrato de la misma persona — primer plano, ambiental y creativo.",
        difficulty: "beginner",
        objective: "Producir 3 retratos mostrando diferentes estados de animo usando luz natural y fondos simples.",
        settings: "ISO 100-400 | f/1.8-f/2.8 para desenfoque | 1/200 seg | 50-85mm distancia focal",
        tips: [
          "Dispara durante la hora dorada o en sombra abierta para luz suave y favorecedora.",
          "Enfoca en los ojos — siempre. Usa AF de punto único en el ojo más cercano.",
          "Dirige a tu sujeto: que mire ligeramente fuera de cámara para un aire natural.",
          "Para primeros planos, mantener el fondo simple y limpio.",
          "Para retratos ambientales, incluye el entorno que cuente una historia sobre la persona.",
        ],
        deliverables: "3 retratos editados: 1 primer plano cerrado, 1 ambiental (más amplio, sujeto en contexto), 1 creativo (ángulo, luz o estado de animo inusual).",
      },
      {
        id: "landscape",
        icon: "Mountains",
        title: "Fotografía de Paisaje",
        desc: "Capturar un paisaje en hora dorada que demuestre profundidad, líneas guía y exposición correcta.",
        difficulty: "beginner",
        objective: "Disparar 5 imágenes de paisaje durante hora dorada mostrando primer plano, plano medio y fondo.",
        settings: "ISO 100 | f/8-f/11 para nitidez | Trípode recomendado | 1/30-1/125 seg | Gran angular (16-35mm)",
        tips: [
          "Llega 30 minutos antes de la hora dorada para explorar tu composición.",
          "Usa f/8-f/11 para máxima nitidez en todo el encuadre.",
          "Incluye un elemento fuerte en primer plano (rocas, flores, agua) para dar profundidad.",
          "Coloca el horizonte en el tercio superior o inferior — nunca en el centro exacto.",
          "Bracketing de exposición (+/- 1 paso) en escenas de alto contraste.",
        ],
        deliverables: "5 paisajes editados: 2 vistas amplias, 1 con líneas guía, 1 con primer plano fuerte, 1 panorámica o vertical.",
      },
      {
        id: "fashion",
        icon: "TShirt",
        title: "Moda y Estilo Urbano",
        desc: "Fotografiar 5 looks de outfits con fondos urbanos que destaquen tanto la ropa como el entorno.",
        difficulty: "intermediate",
        objective: "Crear un mini lookbook: 5 fotos de moda de cuerpo completo con poses variadas, ángulos y locaciones urbanas.",
        settings: "ISO 100-400 | f/2-f/4 | 1/250 seg | 50-85mm distancia focal",
        tips: [
          "Busca paredes coloridas, escaleras o superficies texturizadas como fondos.",
          "Dispara a nivel de ojo o ligeramente desde abajo para que el modelo luzca más alto.",
          "Dirige movimiento: caminar, girar, ajustar un accesorio añade energía.",
          "Asegúrate de que la ropa sea la protagonista — el fondo debe complementar, no competir.",
          "Los cielos nublados son tu amigo: luz uniforme y suave sin sombras duras.",
        ],
        deliverables: "5 imágenes editadas: mezcla de cuerpo completo y 3/4, al menos 2 locaciones diferentes.",
      },
      {
        id: "street",
        icon: "Camera",
        title: "Fotografía Callejera",
        desc: "Documentar momentos espontáneos de la vida cotidiana en un espacio público sin escenificar ni dirigir sujetos.",
        difficulty: "intermediate",
        objective: "Disparar 10 imágenes espontáneas en 1 hora que capturen emoción, historia o yuxtaposición interesante.",
        settings: "ISO 400-800 | f/5.6-f/8 | 1/250 seg | 35-50mm distancia focal | Enfoque de zona",
        tips: [
          "Pre-configura exposición y enfoque a una distancia de zona (3-5 metros) para disparar rápido.",
          "Busca contrastes: viejo/nuevo, luz/sombra, movimiento/quietud.",
          "Se paciente — espera en un lugar interesante a que un momento se desarrolle.",
          "Dispara desde la cadera para momentos espontáneos discretos.",
          "Respeta el espacio de las personas. Si alguien esta incomodo, sigue adelante.",
        ],
        deliverables: "10 sin editar + 5 seleccionadas con edición básica. Sin escenificación — todas deben ser espontáneas.",
      },
      {
        id: "architecture",
        icon: "Buildings",
        title: "Fotografía de Arquitectura",
        desc: "Capturar líneas de edificios, simetría y patrones geométricos con encuadre preciso.",
        difficulty: "intermediate",
        objective: "Fotografiar 5 edificios o estructuras enfatizando líneas, simetría y repetición geométrica.",
        settings: "ISO 100-200 | f/8-f/11 | Trípode recomendado | Gran angular (16-24mm) o teleobjetivo para compresión",
        tips: [
          "Nivela tu cámara — las verticales inclinadas son el error #1 de principiante.",
          "Usa gran angular de cerca para perspectiva dramática, o tele de lejos para compresión.",
          "Dispara en hora azul para colores dramaticos de cielo detrás de edificios iluminados.",
          "Busca patrones repetitivos: ventanas, columnas, escaleras.",
          "Incluye una figura humana para escala en al menos una toma.",
        ],
        deliverables: "5 imágenes editadas: 1 simétrica, 1 con líneas guía, 1 detalle/patron, 1 con escala humana, 1 en hora azul o luz dramática.",
      },
      {
        id: "wildlife",
        icon: "PawPrint",
        title: "Fauna y Naturaleza",
        desc: "Fotografiar animales o insectos en su hábitat natural con paciencia y reflejos rápidos.",
        difficulty: "advanced",
        objective: "Capturar 5 imágenes de animales o insectos mostrando comportamiento, detalle o interacción con el entorno.",
        settings: "ISO 400-1600 | f/4-f/5.6 | 1/500+ seg para acción | 200-400mm teleobjetivo o macro",
        tips: [
          "Muevete lenta y silenciosamente. Deja que el animal venga a ti.",
          "Usa enfoque continuo (AF-C) para rastrear sujetos en movimiento.",
          "Baja al nivel de los ojos del animal para una perspectiva más intima.",
          "Dispara en modo ráfaga — el comportamiento ocurre en fracciones de segundo.",
          "Temprano en la mañana y al final de la tarde es cuando los animales están más activos.",
        ],
        deliverables: "5 imágenes editadas: 1 retrato (detalle de ojo), 1 acción/comportamiento, 1 en entorno, 1 detalle macro, 1 creativa (silueta o contraluz).",
      },
      {
        id: "product",
        icon: "Package",
        title: "Fotografía de Producto",
        desc: "Disparar imágenes limpias de productos adecuadas para tienda online usando iluminación casera simple.",
        difficulty: "beginner",
        objective: "Crear 5 imágenes de producto sobre fondo limpio (blanco u oscuro) con iluminación uniforme sin sombras.",
        settings: "ISO 100-200 | f/5.6-f/8 | Trípode | 1/60-1/125 seg | 50-100mm",
        tips: [
          "Usa una cartulina blanca curvada en forma de 'curva infinita' para un fondo sin costuras.",
          "Dos lámparas de escritorio con difusores de papel blanco = iluminación de estudio accesible.",
          "Dispara desde múltiples ángulos: frente, 45 grados, cenital, detalle de cerca.",
          "Mantener el producto limpio: huellas y polvo se notan en las fotos.",
          "Para productos reflectantes, usa una carpa de luz o rodea con cartulinas blancas.",
        ],
        deliverables: "5 imágenes editadas por producto: frente, ángulo, cenital, detalle, toma de contexto lifestyle.",
      },
      {
        id: "night",
        icon: "Moon",
        title: "Noche y Larga Exposición",
        desc: "Crear imágenes nocturnas dramaticas usando largas exposiciones, estelas de luz y técnicas creativas.",
        difficulty: "advanced",
        objective: "Producir 5 imágenes nocturnas: estelas de luz, estrellas, light painting, ciudad en hora azul, y una larga exposición creativa.",
        settings: "ISO 100-800 | f/8-f/16 para estelas, f/2.8 para estrellas | 10-30 seg (o bulb) | Trípode esencial",
        tips: [
          "Usa trípode y disparador remoto (o temporizador de 2 segundos) para evitar vibración.",
          "Para estelas de luz: 10-30 segundos a f/8, ISO 100 cerca de una calle transitada.",
          "Para estrellas: f/2.8, ISO 1600-3200, 15-25 segundos (usa Regla 500: 500/distancia focal = segundos maximos).",
          "Dispara en hora azul (20-40 min después del atardecer) para el mejor contraste cielo-ciudad.",
          "Light painting: usa una linterna durante una larga exposición para 'pintar' luz sobre tu sujeto.",
        ],
        deliverables: "5 imágenes editadas: 1 estela de luz, 1 cielo estrellado, 1 light painting, 1 paisaje urbano en hora azul, 1 creativa.",
      },
      {
        id: "macro",
        icon: "MagnifyingGlassPlus",
        title: "Fotografía Macro",
        desc: "Explorar el mundo miniatura de acercamientos extremos revelando texturas y detalles invisibles a simple vista.",
        difficulty: "intermediate",
        objective: "Capturar 5 imágenes macro de diferentes sujetos mostrando textura, patrón y detalle extremo.",
        settings: "ISO 200-400 | f/8-f/11 para más profundidad | 1/200+ seg (o trípode) | Lente macro o tubos de extensión",
        tips: [
          "La profundidad de campo es mínima en macro — usa f/8 o menor y apila enfoque si es posible.",
          "Usa enfoque manual y mueve tu cuerpo adelante/atrás para clavar el plano de enfoque.",
          "Días nublados o sombra proporcionan luz uniforme y difusa que revela textura.",
          "Flores, insectos, gotas de agua, texturas de alimentos y patrones de tela son grandes sujetos.",
          "Un trípode con cabezal de bola es casi esencial para trabajo macro nítido.",
        ],
        deliverables: "5 imágenes macro editadas: 1 insecto, 1 detalle de flor, 1 textura (tela/madera/metal), 1 gota de agua, 1 acercamiento de comida.",
      },
      {
        id: "sports",
        icon: "Lightning",
        title: "Deportes y Acción",
        desc: "Congelar sujetos en rápido movimiento con altas velocidades de obturador y enfoque continuo.",
        difficulty: "advanced",
        objective: "Capturar 5 tomas de acción que congelen el momento cumbre y transmitan energía y atletismo.",
        settings: "ISO 400-1600 | f/2.8-f/4 | 1/1000+ seg | AF-C rastreo continuo | 70-200mm",
        tips: [
          "Usa Prioridad de Velocidad (Tv/S): configura 1/1000+ y deja que la cámara elija apertura.",
          "AF Continuo (AF-C / AI Servo) es innegociable para sujetos en movimiento.",
          "Anticipa el momento cumbre: un jugador de basketball en el punto más alto de su salto.",
          "Haz paneo con el sujeto para desenfoque de movimiento en el fondo pero sujeto nítido.",
          "Dispara en modo ráfaga — captura 5-10 cuadros por segundo para atrapar el momento decisivo.",
        ],
        deliverables: "5 imágenes de acción editadas: 1 momento cumbre congelado, 1 paneo (fondo borroso), 1 preparación, 1 celebración/emoción, 1 toma amplia de establecimiento.",
      },
      {
        id: "event",
        icon: "UsersThree",
        title: "Fotografía de Eventos",
        desc: "Documentar una reunión social capturando momentos clave, emociones espontáneas y fotos grupales.",
        difficulty: "intermediate",
        objective: "Cubrir un evento social (fiesta, reunión familiar, evento escolar) con variedad de tomas que cuenten la historia.",
        settings: "ISO 800-3200 | f/2.8-f/4 | 1/125-1/250 seg | Flash rebotado en interiores | 24-70mm",
        tips: [
          "Llega temprano para explorar iluminación y encontrar buenos ángulos antes de que lleguen los invitados.",
          "Captura las tres fases: preparación/anticipación, momentos clave, reacciones espontáneas.",
          "Rebota tu flash en el techo para luz suave y natural en interiores.",
          "Obtener tomas amplias (ambiente/atmósfera) y cerradas (emoción/detalle).",
          "Sigue moviéndote — no te quedes en un solo lugar todo el evento.",
        ],
        deliverables: "10 imágenes editadas: 2 amplias de ambiente, 3 momentos espontáneos, 2 grupos posados, 2 detalle/decoración, 1 creativa (silueta, reflejo o movimiento).",
      },
      {
        id: "blackwhite",
        icon: "CircleHalf",
        title: "Fotografía en Blanco y Negro",
        desc: "Dispara una serie de cuadros usando solo monocromo — enfocate en el rango tonal, el contraste, las formas de las sombras y la textura en lugar del color.",
        difficulty: "intermediate",
        objective: "Crear un conjunto coherente de 8 imágenes monocromas donde la luz, el contraste y la textura — no el color — sostengan todo el encuadre.",
        settings: "ISO 100-800 | Dispara RAW + configura el Estilo de Imagen/Perfil de la cámara en Monocromo para una vista previa B/N | Mide para las altas luces | Cualquier distancia focal",
        tips: [
          "Entrena tu ojo para ver tonos, no colores: entrecierra los ojos ante la escena para juzgar como se separan las zonas claras y oscuras.",
          "Busca contraste y textura — la luz lateral en hora dorada revela forma, aspereza y volumen que el color oculta.",
          "Usa el Estilo de Imagen Monocromo para que la pantalla muestre B/N, pero sigue disparando RAW para conservar todos los datos en la conversión.",
          "Las sombras son un sujeto: deja que los negros sean realmente negros y usa el espacio negativo a propósito.",
          "Los filtros de color (o su equivalente digital) cambian las relaciones tonales — un filtro rojo oscurece los cielos azules para nubes dramaticas.",
        ],
        deliverables: "8 imágenes monocromas editadas: 2 de alto contraste, 2 de clave baja (dominadas por sombra), 2 estudios de textura/detalle, 1 de clave alta (luminosa), 1 retrato ambiental.",
      },
    ],
  },
};

const iconMap = {
  ForkKnife,
  User,
  Mountains,
  TShirt,
  Camera,
  Buildings,
  PawPrint,
  Package,
  Moon,
  MagnifyingGlassPlus,
  Lightning,
  UsersThree,
  CircleHalf,
};

export default function ProjectsPage() {
  const { lang } = useTranslation();
  const { user } = useAuth();
  const signedIn = !!user && user !== false;
  const c = projects[lang] || projects.en;
  const [expanded, setExpanded] = useState(null);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    if (!signedIn) return;
    axios.get("/api/projects/progress", { withCredentials: true })
      .then((r) => setProgress(r.data.progress || {}))
      .catch(() => {});
  }, [signedIn]);

  const toggle = (id) => setExpanded(expanded === id ? null : id);

  const toggleItem = async (pid, idx) => {
    const prev = progress[pid] || [];
    const next = prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx];
    setProgress((p) => ({ ...p, [pid]: next }));
    try {
      await axios.post("/api/projects/progress", { project_id: pid, checked: next }, { withCredentials: true });
    } catch {
      setProgress((p) => ({ ...p, [pid]: prev }));
      toast.error(lang === "es" ? "No se pudo guardar tu progreso" : "Couldn't save your progress");
    }
  };

  const extrasFor = (id) => (projectExtras[id] || {})[lang] || (projectExtras[id] || {}).en;
  const totals = c.items.reduce(
    (acc, p) => {
      const n = extrasFor(p.id)?.checklist.length || 0;
      const d = (progress[p.id] || []).filter((i) => i < n).length;
      return { items: acc.items + n, done: acc.done + d, projects: acc.projects + (n > 0 && d === n ? 1 : 0) };
    },
    { items: 0, done: 0, projects: 0 }
  );

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="projects-page">
      <motion.div variants={container} initial="hidden" animate="show" className="space-y-6">
        <motion.div variants={item}>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-2">
            {c.page_title}
          </h1>
          <p className="text-[#71717A] text-base">{c.page_sub}</p>
        </motion.div>

        {signedIn && (
          <motion.div variants={item}>
            <ProjectProgressSummary
              projectsDone={totals.projects}
              projectsTotal={c.items.length}
              itemsDone={totals.done}
              itemsTotal={totals.items}
            />
          </motion.div>
        )}

        <div className="grid grid-cols-1 gap-4">
          {c.items.map((proj) => {
            const Icon = iconMap[proj.icon] || Camera;
            const dc = difficultyColors[proj.difficulty];
            const isOpen = expanded === proj.id;
            const extra = extrasFor(proj.id);
            const checked = (progress[proj.id] || []).filter((i) => i < (extra?.checklist.length || 0));

            return (
              <motion.div
                key={proj.id}
                variants={item}
                className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden hover:border-white/15 transition-colors"
                data-testid={`project-${proj.id}`}
              >
                <button
                  onClick={() => toggle(proj.id)}
                  className="w-full flex items-center gap-4 p-5 text-left"
                  data-testid={`project-toggle-${proj.id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#F5A524]/10 flex items-center justify-center shrink-0">
                    <Icon weight="fill" className="w-5 h-5 text-[#F5A524]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-['Outfit'] text-white font-medium">{proj.title}</h3>
                      <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${dc.bg} ${dc.text} ${dc.border} border`}>
                        {c[proj.difficulty]}
                      </span>
                    </div>
                    <p className="text-[#71717A] text-sm truncate">{proj.desc}</p>
                  </div>
                  {signedIn && extra && (
                    <span
                      className={`font-['JetBrains_Mono'] text-xs px-2 py-0.5 rounded-md shrink-0 ${checked.length === extra.checklist.length ? "bg-emerald-500/10 text-emerald-400" : "bg-white/5 text-[#A1A1AA]"}`}
                      data-testid={`project-progress-${proj.id}`}
                    >
                      {checked.length}/{extra.checklist.length}
                    </span>
                  )}
                  {isOpen ? (
                    <CaretUp className="w-5 h-5 text-[#71717A] shrink-0" />
                  ) : (
                    <CaretDown className="w-5 h-5 text-[#71717A] shrink-0" />
                  )}
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-5 space-y-4 border-t border-white/5 pt-4">
                        {/* Challenge */}
                        {extra && (
                          <div className="border-l-2 border-[#F5A524] pl-3" data-testid={`project-challenge-${proj.id}`}>
                            <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{lang === "es" ? "Reto" : "Challenge"}</h4>
                            <p className="text-white text-sm">{extra.challenge}</p>
                          </div>
                        )}
                        {/* Objective */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.objective}</h4>
                          <p className="text-[#A1A1AA] text-sm">{proj.objective}</p>
                        </div>
                        {/* Suggested Settings */}
                        <div>
                          <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                            <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest">{c.settings_hint}</h4>
                            <GaleriaShareButton testId={`project-share-galeria-${proj.id}`} />
                          </div>
                          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2">
                            <code className="font-['JetBrains_Mono'] text-xs text-white">{proj.settings}</code>
                          </div>
                        </div>
                        {/* Tips */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.tips}</h4>
                          <div className="space-y-1.5">
                            {proj.tips.map((tip, i) => (
                              <div key={i} className="flex items-start gap-2">
                                <Star weight="fill" className="w-3 h-3 text-[#F5A524] mt-1 shrink-0" />
                                <p className="text-[#A1A1AA] text-sm">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Deliverables */}
                        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-3">
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-widest mb-1">{c.deliverables}</h4>
                          <p className="text-white text-sm">{proj.deliverables}</p>
                        </div>
                        {extra && (
                          <ProjectChecklist
                            projectId={proj.id}
                            items={extra.checklist}
                            checked={checked}
                            signedIn={signedIn}
                            onToggle={(i) => toggleItem(proj.id, i)}
                          />
                        )}
                        <GaleriaDelivery projectId={proj.id} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}
